import { GA4React } from 'ga-4-react';
import { env, isProdEnvironment } from '~/env';
import { extractLanguage } from '~/features/lang/lang';
import { getLocale } from '~/features/lang/selectors';
import { configureFetchLanguageData } from '~/features/lang/services';
import { userProfileObtained, userSessionExpired, userSessionRenewed } from '~/features/login/actions';
import { configureMockUserManager, configureUserManager, createUserManager } from '~/features/login/login';
import { accessToken } from '~/features/tokenHandling/accessToken';
import { accessTokenStored, idTokenStored } from '~/features/tokenHandling/actions';
import { attemptInitialSignIn } from '~/setup/oauth';
import { store } from '~/setup/store';
const { useValidToken } = env.featureToggles;
import '~/style/css/main.less'; // TODO Maybe move away to actual app

let googleAnalyticsInstance;

const isGoogleAnalyticsActive = () => {
    const { location: { host } } = window;
    return googleAnalyticsInstance && isProdEnvironment(host);
};

export const sendGoogleAnalyticsEvent = (pageTitle, method) => {
    if (isGoogleAnalyticsActive()) {
        googleAnalyticsInstance.gtag('event', pageTitle, { 'method': method });
    }
};

export const sendGoogleAnalyticsPageView = (path) => {
    if (isGoogleAnalyticsActive()) {
        googleAnalyticsInstance.pageview(path);
    }
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export const main = async(renderApp) => {
    // Initialise Google Analytics
    const ga4react = new GA4React('G-6323F9NV7L');

    const fetchLanguageData = configureFetchLanguageData(store);

    // We want the `<html lang>` attribute to be synced with the
    // language currently displayed
    store.subscribe(() => {
        const lang = extractLanguage(getLocale(store.getState()));
        const html = document.querySelector('html');

        if (html?.getAttribute('lang') !== lang) {
            html?.setAttribute('lang', lang);
        }
    });

    const oauthConfig = {
        onSessionExpired: () => {
            accessToken.discardAccessToken();
            store.dispatch(userSessionExpired());
        },
        onSessionRenewed: result => {
            accessToken.saveAccessToken(result.accessToken);
            store.dispatch(accessTokenStored(result.accessToken));
            store.dispatch(idTokenStored(result.idToken));
            store.dispatch(userProfileObtained(result.profile));
            store.dispatch(userSessionRenewed());

            // You will need to get the user language by yourself then
            // you may fetch the suitable messages from the CDN. Depending
            // on when and from where you fetch the user settings you might
            // want to employ a loading spinner while the request is ongoing.
            fetchLanguageData(result.locale);
        },
    };

    const isAllowedToMockAuth = !import.meta.env.PROD || useValidToken;
    const userManager =
        isAllowedToMockAuth && env.runtimeConfig.login.mockAuthorization ?
            configureMockUserManager(oauthConfig) :
            configureUserManager(oauthConfig, createUserManager());

    try {
        await userManager.clearStaleState();
        await attemptInitialSignIn(userManager);

        if (isGoogleAnalyticsActive()) {
            (async() => {
                await ga4react.initialize()
                    .then((ga4) => {
                        googleAnalyticsInstance = ga4;
                    })
                    .catch(err =>
                        console.error('Analytics Failure.', err),
                    )
                    .finally(() => {
                        renderApp();
                    });
            })();
        } else {
            renderApp();
        }
    } catch (error) {
        console.error('Could not start application', error);
    }
};
